import React, { useState, useEffect, useRef } from "react";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import Select from "react-select";
import countries from "../../utils/countryList";
import { 
  FaUser, FaBuilding, FaIdCard, FaMapMarkerAlt, FaPhone, FaEnvelope, 
  FaBriefcase, FaMoneyBill, FaGraduationCap, FaUsers, FaTimes, 
  FaInfoCircle, FaCheckCircle, FaExclamationTriangle, FaCalendarAlt,
  FaEdit
} from "react-icons/fa";

const countryOptions = countries.map((country) => ({
  label: `${country.flag} ${country.name}`,
  value: country.name,
}));

const Section = ({ title, icon, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-5 py-3 bg-gray-50 font-semibold text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <span className="text-orange-500">{icon}</span>
          <span>{title}</span>
        </div>
        <span className="text-gray-500 text-lg">{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>}
    </div>
  );
};

// Helper: calculate contract duration string from two dates
const calculateContractDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end) || end <= start) return "";

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    months -= 1;
    days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`);
  if (days > 0 && years === 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
  return parts.join(", ");
};

export default function EditStaffModal({ staffId, closeModal, onUpdateSuccess }) {
  const maxDate = "2026-12-31";
  const modalRef = useRef(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [closeModal]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    async function fetchStaff() {
      try {
        const res = await getOneStaff(staffId);
        const data = res.data || res;

        const startDate = formatDate(data.contractStartDate);
        const endDate = formatDate(data.contractEndDate);

        // Flatten the data from the backend to match the form structure
        const flattenedData = {
          ...data,
          // Personal Info
          dateOfBirth: formatDate(data.dateOfBirth),
          // Residence
          villageOrBlock: data.residence?.villageOrBlock || "",
          payam: data.residence?.payam || "",
          county: data.residence?.county || "",
          state: data.residence?.state || "",
          // Employment Details
          jobTitle: data.jobTitle || "",
          department: data.department || "",
          dateOfEmployment: formatDate(data.dateOfEmployment),
          employmentType: data.employmentType || "",
          contractType: data.contractType || "",
          contractStartDate: startDate,
          contractEndDate: endDate,
          contractDuration: data.contractDuration || calculateContractDuration(startDate, endDate),
          salaryRange: data.salaryRange || "",
          // Banking Details
          bankName: data.bankingDetails?.[0]?.bankName || "",
          bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
          accountName: data.bankingDetails?.[0]?.accountName || "",
          accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
          currency: data.bankingDetails?.[0]?.currency || "",
          swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
          // Qualifications
          institution: data.qualifications?.[0]?.institution || "",
          award: data.qualifications?.[0]?.award || "",
          yearFrom: data.qualifications?.[0]?.yearFrom || "",
          yearTo: data.qualifications?.[0]?.yearTo || "",
          // Work History
          keyExpertise: data.workHistory?.keyExpertise || "",
          recentEmployer: data.workHistory?.recentEmployer || "",
          workrole: data.workHistory?.workrole || "",
          periodOfService: data.workHistory?.periodOfService || "",
          recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
          // Next of Kin
          nokName: data.nextOfKin?.name || "",
          nokSex: data.nextOfKin?.sex || "",
          nokNationality: data.nextOfKin?.nationality || "",
          nokRelationship: data.nextOfKin?.relationship || "",
          nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
          nokPayam: data.nextOfKin?.residence?.payam || "",
          nokCounty: data.nextOfKin?.residence?.county || "",
          nokState: data.nextOfKin?.residence?.state || "",
          nokTel: data.nextOfKin?.tel || "",
          nokMobile: data.nextOfKin?.mobile || "",
          nokEmail: data.nextOfKin?.email || "",
        };
        setFormData(flattenedData);
      } catch (err) {
        setError("Failed to load staff info");
      } finally {
        setLoading(false);
      }
    }
    fetchStaff();
  }, [staffId]);

  useEffect(() => {
    let timer;
    if (error || success) {
      timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [error, success]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Auto-recalculate contractDuration whenever start or end date changes
      if (name === "contractStartDate" || name === "contractEndDate") {
        const start = name === "contractStartDate" ? value : prev.contractStartDate;
        const end = name === "contractEndDate" ? value : prev.contractEndDate;
        updated.contractDuration = calculateContractDuration(start, end);
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);

    // Validate end date is after start date if both are set
    if (formData.contractStartDate && formData.contractEndDate) {
      if (new Date(formData.contractEndDate) <= new Date(formData.contractStartDate)) {
        setError("Contract end date must be after the start date.");
        setSubmitting(false);
        return;
      }
    }

    // Restructure the data back to the nested format before sending
    const updatedStaff = {
      ...formData,
      residence: {
        villageOrBlock: formData.villageOrBlock,
        payam: formData.payam,
        county: formData.county,
        state: formData.state,
      },
      nextOfKin: {
        name: formData.nokName,
        sex: formData.nokSex,
        nationality: formData.nokNationality,
        relationship: formData.nokRelationship,
        residence: {
          villageOrBlock: formData.nokVillageOrBlock,
          payam: formData.nokPayam,
          county: formData.nokCounty,
          state: formData.nokState,
        },
        tel: formData.nokTel,
        mobile: formData.nokMobile,
        email: formData.nokEmail,
      },
      bankingDetails: [{
        bankName: formData.bankName,
        bankBranch: formData.bankBranch,
        accountName: formData.accountName,
        accountNumber: formData.accountNumber,
        currency: formData.currency,
        swiftCode: formData.swiftCode,
      }],
      qualifications: [{
        yearFrom: formData.yearFrom,
        yearTo: formData.yearTo,
        institution: formData.institution,
        award: formData.award,
      }],
      workHistory: {
        keyExpertise: formData.keyExpertise,
        recentEmployer: formData.recentEmployer,
        workrole: formData.workrole,
        periodOfService: formData.periodOfService,
        recentGrossAnnualPaymentRange: formData.recentGrossAnnualPaymentRange,
      },
    };

    try {
      await updateStaff(staffId, updatedStaff);
      setSuccess("Staff updated successfully!");
      if (onUpdateSuccess) onUpdateSuccess();
      setTimeout(closeModal, 2000);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "An error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg font-medium text-gray-700">Loading staff information...</p>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  const renderField = (label, name, type = "text", options, icon) => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}
        {options ? (
          <select
            name={name}
            value={formData[name] || ""}
            onChange={handleChange}
            className={`w-full border rounded-lg px-3 py-2.5 ${icon ? 'pl-10' : 'pl-3'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors`}
          >
            <option value="">-- Select {label} --</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            name={name}
            type={type}
            value={formData[name] || ""}
            onChange={handleChange}
            max={type === "date" ? maxDate : undefined}
            className={`w-full border rounded-lg px-3 py-2.5 ${icon ? 'pl-10' : 'pl-3'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors`}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Popup Notifications */}
      {error && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <FaCheckCircle className="text-green-500 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header with Orange Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <FaEdit className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Edit Staff</h2>
                <p className="text-black-100 mt-1">Staff Management System</p>
              </div>
            </div>
            <button
              onClick={closeModal}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          <form id="edit-staff-form" onSubmit={handleSubmit} className="space-y-6">
            <Section title="Personal Information" icon={<FaUser />}>
              {renderField("First Name", "firstName", "text", null, <FaUser className="h-4 w-4 text-gray-400" />)}
              {renderField("Last Name", "lastName", "text", null, <FaUser className="h-4 w-4 text-gray-400" />)}
              {renderField("Email", "email", "email", null, <FaEnvelope className="h-4 w-4 text-gray-400" />)}
              {renderField("Gender", "gender", "text", ["Male", "Female"], <FaUser className="h-4 w-4 text-gray-400" />)}
              {renderField("Date of Birth", "dateOfBirth", "date", null, <FaIdCard className="h-4 w-4 text-gray-400" />)}
              {renderField("Role", "role", "text", [
                "admin",
                "hr_officer",
                "directors",
                "head_of_operations",
                "finance_officer",
                "operations_associate",
                "project_officer",
                "field_officer",
                "program_director",
                "program_manager"
              ], <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Marital Status", "maritalStatus", "text", ["Single", "Married", "Other"], <FaUser className="h-4 w-4 text-gray-400" />)}

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Nationality
                </label>
                <Select
                  options={countryOptions}
                  value={countryOptions.find((opt) => opt.value === formData.nationality) || null}
                  onChange={(selected) => {
                    setFormData({ ...formData, nationality: selected?.value || "" });
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select nationality..."
                  isClearable
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      minHeight: '42px',
                      borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                      '&:hover': {
                        borderColor: '#F97316'
                      }
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#9CA3AF'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#1F2937'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                      color: state.isSelected ? 'white' : '#1F2937',
                      '&:hover': {
                        backgroundColor: '#FFEDD5',
                        color: '#1F2937'
                      }
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#6B7280',
                      '&:hover': {
                        color: '#F97316'
                      }
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#D1D5DB'
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      border: '1px solid #E5E7EB'
                    })
                  }}
                />
              </div>
              {renderField("National ID or Passport", "nationalIdOrPassport", "text", null, <FaIdCard className="h-4 w-4 text-gray-400" />)}
              {renderField("Village or Block", "villageOrBlock", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Payam", "payam", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("County", "county", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("State", "state", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Telephone", "tel", "text", null, <FaPhone className="h-4 w-4 text-gray-400" />)}
              {renderField("Mobile", "mobile", "text", null, <FaPhone className="h-4 w-4 text-gray-400" />)}
            </Section>

            <Section title="Employment Details" icon={<FaBriefcase />}>
              {renderField("Job Title", "jobTitle", "text", ["Administration", "Finance Officer", "Human Resources", "Program Manager", "Operations Associate", "Head of Operations", "Program Director", "Field Officer"], <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Department", "department", "text", null, <FaBuilding className="h-4 w-4 text-gray-400" />)}
              {renderField("Date of Employment", "dateOfEmployment", "date", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Employment Type", "employmentType", "text", ["Full-time", "Part-time", "Contract", "Internship"], <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Contract Type", "contractType", "text", ["Fixed Term", "Permanent", "Probationary", "Consultancy"], <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Contract Start Date", "contractStartDate", "date", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Contract End Date", "contractEndDate", "date", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}

              {/* Contract Duration — read-only, auto-calculated */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Contract Duration</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaCalendarAlt className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.contractDuration || ""}
                    readOnly
                    placeholder="Auto-calculated from start and end dates"
                    className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 pl-10 text-gray-500 cursor-not-allowed focus:outline-none"
                  />
                </div>
              </div>

              {renderField("Gross Salary", "salaryRange", "text", null, <FaMoneyBill className="h-4 w-4 text-gray-400" />)}
            </Section>

            <Section title="Bank Details" icon={<FaMoneyBill />}>
              {renderField("Bank Name", "bankName", "text", null, <FaBuilding className="h-4 w-4 text-gray-400" />)}
              {renderField("Bank Branch", "bankBranch", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Account Name", "accountName", "text", null, <FaUser className="h-4 w-4 text-gray-400" />)}
              {renderField("Account Number", "accountNumber", "text", null, <FaIdCard className="h-4 w-4 text-gray-400" />)}
              {renderField("Currency", "currency", "text", null, <FaMoneyBill className="h-4 w-4 text-gray-400" />)}
              {renderField("SWIFT Code", "swiftCode", "text", null, <FaIdCard className="h-4 w-4 text-gray-400" />)}
            </Section>

            <Section title="Education & Work History" icon={<FaGraduationCap />}>
              {renderField("Institution", "institution", "text", null, <FaGraduationCap className="h-4 w-4 text-gray-400" />)}
              {renderField("Award", "award", "text", null, <FaGraduationCap className="h-4 w-4 text-gray-400" />)}
              {renderField("Year From", "yearFrom", "text", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Year To", "yearTo", "text", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Key Expertise", "keyExpertise", "text", null, <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Recent Employer", "recentEmployer", "text", null, <FaBuilding className="h-4 w-4 text-gray-400" />)}
              {renderField("Role", "workrole", "text", null, <FaBriefcase className="h-4 w-4 text-gray-400" />)}
              {renderField("Period of Service", "periodOfService", "text", null, <FaCalendarAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Recent Gross Annual Payment Range", "recentGrossAnnualPaymentRange", "text", null, <FaMoneyBill className="h-4 w-4 text-gray-400" />)}
            </Section>

            <Section title="Next of Kin Information" icon={<FaUsers />}>
              {renderField("Next of Kin Name", "nokName", "text", null, <FaUser className="h-4 w-4 text-gray-400" />)}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Next of Kin Nationality
                </label>
                <Select
                  options={countryOptions}
                  value={countryOptions.find((opt) => opt.value === formData.nokNationality) || null}
                  onChange={(selected) => {
                    setFormData({ ...formData, nokNationality: selected?.value || "" });
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select nationality..."
                  isClearable
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      minHeight: '42px',
                      borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                      '&:hover': {
                        borderColor: '#F97316'
                      }
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#9CA3AF'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#1F2937'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                      color: state.isSelected ? 'white' : '#1F2937',
                      '&:hover': {
                        backgroundColor: '#FFEDD5',
                        color: '#1F2937'
                      }
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#6B7280',
                      '&:hover': {
                        color: '#F97316'
                      }
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      backgroundColor: '#D1D5DB'
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      border: '1px solid #E5E7EB'
                    })
                  }}
                />
              </div>
              {renderField("Next of Kin Relationship", "nokRelationship", "text", null, <FaUsers className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin Village or Block", "nokVillageOrBlock", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin Payam", "nokPayam", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin County", "nokCounty", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin State", "nokState", "text", null, <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin Telephone", "nokTel", "text", null, <FaPhone className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin Mobile", "nokMobile", "text", null, <FaPhone className="h-4 w-4 text-gray-400" />)}
              {renderField("Next of Kin Email", "nokEmail", "email", null, <FaEnvelope className="h-4 w-4 text-gray-400" />)}
            </Section>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FaInfoCircle className="w-4 h-4 text-orange-500" />
                <span>Make sure to save your changes</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                form="edit-staff-form"
                className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                  submitting
                    ? 'bg-orange-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                }`}
                disabled={submitting}
              >
                {submitting ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving Changes...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <FaEdit />
                    <span>Save Changes</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}